import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

export type SystemAudioStatus = "disconnected" | "active" | "ended";
export type SystemAudioSurface = "tab" | "screen" | null;

export const useSystemAudio = () => {
  const [status, setStatus] = useState<SystemAudioStatus>("disconnected");
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [surface, setSurface] = useState<SystemAudioSurface>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const connect = useCallback(async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getDisplayMedia({
        audio: {
          suppressLocalAudioPlayback: false,
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          // eslint-disable-next-line no-undef
        } as MediaTrackConstraints,
        // Minimal video — required by some browsers alongside audio capture.
        // The video track is not used but cannot always be omitted.
        video: { width: 1, height: 1, frameRate: 1 },
      });

      const audioTrack = mediaStream.getAudioTracks()[0];
      if (!audioTrack) {
        mediaStream.getTracks().forEach((t) => t.stop());
        toast.error('No audio captured. Make sure "Share system audio" is checked in the dialog.');
        return;
      }

      // Detect whether the user shared a browser tab or a screen surface.
      const videoTrack = mediaStream.getVideoTracks()[0];
      const displaySurface = videoTrack?.getSettings().displaySurface;
      const detectedSurface: SystemAudioSurface =
        displaySurface === "browser" ? "tab" : displaySurface ? "screen" : null;

      audioTrack.addEventListener("ended", () => {
        streamRef.current = null;
        setStream(null);
        setStatus("ended");
        setSurface(null);
      });

      streamRef.current = mediaStream;
      setStream(mediaStream);
      setStatus("active");
      setSurface(detectedSurface);
    } catch (error) {
      if (error instanceof Error && error.name === "NotAllowedError") {
        return; // user cancelled — no toast needed
      }
      toast.error("Failed to capture system audio");
    }
  }, []);

  const disconnect = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setStream(null);
    setStatus("disconnected");
    setSurface(null);
  }, []);

  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  return { status, stream, surface, connect, disconnect };
};
